import React, { useState } from "react";
import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ClockIcon from "../../../../shared/icons/ClockIcon";
import style from "./HistoryTab.module.scss";
import { XIcon } from "lucide-react";
import CheckIcon from "../../../../shared/icons/CheckIcon";
import ChevronIcon from "../../../../shared/icons/ChevronIcon";
import { getOrderWagonNumbers, isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";

const STATUS_LABEL_KEYS = {
  created: "statusCreated",
  assigned: "statusAssigned",
  assignedExecutor: "statusAssignedExecutor",
  booked: "statusBooked",
  confirmed: "statusConfirmed",
  processLoadingCargo: "statusProcessLoadingCargo",
  onTheTrip: "statusOnTheTrip",
  processUnloadingCargo: "statusProcessUnloadingCargo",
  completedByExecutor: "statusCompletedByExecutor",
  completed: "statusCompleted",
  cancelled: "statusCancelled",
  awaitingOffer: "statusAwaitingOffer",
  return: "statusReturn",
};

const PRICE_STATUS_KEYS = {
  awaiting: "priceChangedAwaiting",
  confirmed: "priceChangedConfirmed",
  rejected: "priceChangedRejected",
};

const PRICE_STATUS_ICON_CLASS = {
  awaiting: "icon_awaiting",
  confirmed: "icon_confirmed",
  rejected: "icon_rejected",
};

const PRICE_STATUS_CARD_CLASS = {
  awaiting: "card_awaiting",
  confirmed: "card_confirmed",
  rejected: "card_rejected",
};

const CANCELED_STATUS_CODE = "canceled";
const RETURN_TO_TENDER_STATUS_CODE = "returnToTender";
const REBID_STATUS_LABEL_KEYS = {
  inrebid: "statusInRebid",
  rebidclosed: "statusRebidClosed",
};

const getPriceIcon = (status) => {
  if (status === "confirmed") return <CheckIcon width="22" height="22" />;
  if (status === "rejected") return <XIcon size={22} />;
  return <ClockIcon fill="#6D7885" type={2} width={26} height={26} />;
};

function getStatusLabel(item, t) {
  if (item.statusName) return item.statusName;
  const normalizedStatusCode = (item.statusCode || "").toLowerCase();
  const labelKey = REBID_STATUS_LABEL_KEYS[normalizedStatusCode] || STATUS_LABEL_KEYS[item.statusCode];
  return labelKey ? t(`orderInfoExpeditor.history.${labelKey}`) : item.statusCode;
}

function formatCreatedAt(createdAt) {
  if (!createdAt) return "";
  return moment(createdAt).format("DD.MM.YYYY, HH:mm");
}

function getDescription(item) {
  const parts = [];
  if (item.moderator) parts.push(item.moderator);
  if (item.addressName) parts.push(item.addressName);
  return parts.join(" • ");
}

const HistoryTab = ({ history = [], order }) => {
  const { t } = useTranslation();
  const [openMap, setOpenMap] = useState({});
  const wagonNumbers = isRailwayOrder(order) ? getOrderWagonNumbers(order) : [];

  if (!history.length) {
    return <div className={style.empty}>{t("orderInfoExpeditor.history.empty")}</div>;
  }

  const lastIndex = history.length - 1;

  return (
    <div className={style.wrapper}>
      {history.map((item, index) => {
        const hasNext = index < lastIndex;

        if (item.statusCode === "priceChanged") {
          const priceStatus = item.changedPriceStatus;
          const statusKey = PRICE_STATUS_KEYS[priceStatus];

          return (
            <div key={item.createdAt + index} className={style.row}>
              <div className={cn(style.left, { [style.left_connected]: hasNext })}>
                <div className={cn(style.icon, style[PRICE_STATUS_ICON_CLASS[priceStatus]])}>
                  {getPriceIcon(priceStatus)}
                </div>
              </div>

              <div className={cn(style.card, style.card_price, style[PRICE_STATUS_CARD_CLASS[priceStatus]])}>
                <div className={style.card_top}>
                  <div className={style.card_title_row}>
                    <span className={style.card_title}>{t("orderInfoExpeditor.history.amountChange")}: {item.changedPriceText}</span>
                    {statusKey && (
                      <span className={cn(style.badge, style[`badge_${priceStatus}`])}>
                        <span className={style.badge_dot} />
                        {t(`orderInfoExpeditor.history.${statusKey}`)}
                      </span>
                    )}
                  </div>
                  <span className={style.card_date}>{formatCreatedAt(item.createdAt)}</span>
                </div>
                {item.comment && (
                  <p className={style.card_desc}>
                    {t("orderInfoExpeditor.history.priceChangedReason")} {item.comment}
                  </p>
                )}
              </div>
            </div>
          );
        }

        const isCanceled = item.statusCode === CANCELED_STATUS_CODE;
        const isReturnToTender = item.statusCode === RETURN_TO_TENDER_STATUS_CODE;
        const normalizedStatusCode = (item.statusCode || "").toLowerCase();
        const hasWagonNumbers = normalizedStatusCode === "confirmed" && wagonNumbers.length > 0;
        const entryKey = item.id || `${item.statusCode}-${item.createdAt}-${index}`;
        const isOpen = openMap[entryKey] ?? index === lastIndex;
        const HeaderTag = hasWagonNumbers ? "button" : "div";
        const isExpeditorRejected = normalizedStatusCode === "expeditorrejected";
        const isInRebid = normalizedStatusCode === "inrebid";
        const isRebidClosed = normalizedStatusCode === "rebidclosed";
        const isRebidStatus = isInRebid || isRebidClosed;
        const isRejected = isCanceled || isReturnToTender || isExpeditorRejected;
        const isActive =
          index === lastIndex && item.statusCode !== "completed" && !isRejected && !isRebidClosed;
        const label = isExpeditorRejected
          ? t("orderInfoExpeditor.history.statusRejectedByExpeditor")
          : isCanceled
            ? t("orderInfoExpeditor.history.statusCanceledByPartner")
            : isReturnToTender
              ? t("orderInfoExpeditor.history.statusReturnToTender")
              : getStatusLabel(item, t);
        const description = isRejected
          ? item.comment && `${t("orderInfoExpeditor.history.priceChangedReason")} ${item.comment}`
          : isRebidStatus
            ? t("orderInfoExpeditor.history.rebidDescription", {
                date: formatCreatedAt(order?.tenderDateEnd),
              })
            : hasWagonNumbers
              ? item.addressName
              : getDescription(item);

        return (
          <div key={item.createdAt + index} className={style.row}>
            <div className={cn(style.left, { [style.left_connected]: hasNext })}>
              <div
                className={cn(style.icon, {
                  [style.icon_active]: isActive,
                  [style.icon_rejected]: isRejected,
                })}
              >
                {isRejected ? <XIcon size={22} /> : <CheckIcon width="22" height="22" />}
              </div>
            </div>

            <div
              className={cn(style.card, {
                [style.card_active]: isActive,
                [style.card_rejected]: isRejected,
              })}
            >
              <HeaderTag
                className={cn(style.card_top, {
                  [style.card_toggle]: hasWagonNumbers,
                  [style.card_top_open]: hasWagonNumbers && isOpen,
                })}
                type={hasWagonNumbers ? "button" : undefined}
                aria-expanded={hasWagonNumbers ? isOpen : undefined}
                onClick={
                  hasWagonNumbers
                    ? () => setOpenMap((prev) => ({ ...prev, [entryKey]: !isOpen }))
                    : undefined
                }
              >
                <span className={style.card_title_row}>
                  <span className={style.card_title}>{label}</span>
                  {isActive && (
                    <span className={style.badge}>
                      <span className={style.badge_dot} />
                      {t("orderInfoExpeditor.history.now")}
                    </span>
                  )}
                </span>
                <span className={style.card_meta}>
                  <span className={style.card_date}>{formatCreatedAt(item.createdAt)}</span>
                  {hasWagonNumbers && (
                    <span className={cn(style.chevron, { [style.chevron_open]: isOpen })} aria-hidden="true">
                      <ChevronIcon />
                    </span>
                  )}
                </span>
              </HeaderTag>
              {description && (!hasWagonNumbers || isOpen) && <p className={style.card_desc}>{description}</p>}
              {hasWagonNumbers && isOpen && (
                <div className={style.wagon_fields}>
                  {wagonNumbers.map((wagonNumber, wagonIndex) => (
                    <div className={style.wagon_field} key={`${wagonNumber}-${wagonIndex}`}>
                      <span className={style.wagon_label}>
                        {t("railwayOrder.wagonNumber")} {wagonIndex + 1}
                      </span>
                      <span className={style.wagon_value}>{wagonNumber}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HistoryTab;
